import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Loader2,
  FileText,
  Info
} from 'lucide-react';

export default function DirectoryImport() {
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [template, setTemplate] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    // Check if user has admin role (roles is an array)
    const userRoles = user?.roles || [user?.role];
    const isAdmin = userRoles.includes('admin');
    
    if (!user || !isAdmin) {
      navigate('/');
      return;
    }
    fetchTemplate();
    fetchCategories();
  }, [user, navigate]);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API}/directory/admin/import-template`);
      setTemplate(response.data);
    } catch (error) {
      console.error('Error fetching template:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/directory/families`);
      const families = response.data;
      const allCategories = [];
      
      for (const family of families) {
        try {
          const familyResponse = await axios.get(`${API}/directory/families/${family.slug}`);
          if (familyResponse.data.categories) {
            for (const cat of familyResponse.data.categories) {
              allCategories.push({
                slug: cat.slug,
                name: `${family.name_fr} > ${cat.name_fr}`
              });
            }
          }
        } catch (e) {
          console.error('Error fetching family:', e);
        }
      }
      
      setCategories(allCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const downloadTemplate = () => {
    if (!template) return;
    
    const blob = new Blob([template.template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'annuaire-import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Template téléchargé');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    const filename = selectedFile.name.toLowerCase();
    if (!filename.endsWith('.csv') && !filename.endsWith('.xlsx')) {
      toast.error('Format non supporté. Utilisez CSV ou XLSX.');
      return;
    }
    setFile(selectedFile);
    setResult(null);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('admin_id', user.id);

      const response = await axios.post(
        `${API}/directory/admin/import-csv?admin_id=${user.id}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setResult(response.data);
      
      if (response.data.imported_count > 0) {
        toast.success(`${response.data.imported_count} entreprise(s) importée(s)`);
      }
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} ligne(s) ignorée(s)`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#0A2540]">Import CSV/Excel - Annuaire</h1>
          <p className="text-gray-600 mt-1">
            Importez plusieurs entreprises d'un coup via un fichier CSV ou Excel
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Import Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Template Download */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Download className="w-5 h-5 text-[#635BFF]" />
                  1. Télécharger le template
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm mb-4">
                  Téléchargez le fichier CSV vide avec les bonnes colonnes, remplissez-le avec vos données, 
                  puis importez-le ci-dessous.
                </p>
                <Button onClick={downloadTemplate} variant="outline">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Télécharger le template CSV
                </Button>
              </CardContent>
            </Card>

            {/* File Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Upload className="w-5 h-5 text-[#635BFF]" />
                  2. Importer le fichier
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Drag & Drop Zone */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive 
                      ? 'border-[#635BFF] bg-[#635BFF]/5' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {file ? (
                    <div className="space-y-2">
                      <FileText className="w-12 h-12 mx-auto text-[#635BFF]" />
                      <p className="font-medium text-[#0A2540]">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024).toFixed(1)} Ko
                      </p>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => { setFile(null); setResult(null); }}
                      >
                        Changer de fichier
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-12 h-12 mx-auto text-gray-400" />
                      <p className="text-gray-600">
                        Glissez votre fichier CSV ou Excel ici
                      </p>
                      <p className="text-sm text-gray-400">ou</p>
                      <label className="cursor-pointer">
                        <span className="text-[#635BFF] hover:underline font-medium">
                          Parcourir...
                        </span>
                        <input
                          type="file"
                          accept=".csv,.xlsx"
                          className="hidden"
                          onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                        />
                      </label>
                    </div>
                  )}
                </div>

                {/* Import Button */}
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    <Info className="w-4 h-4 inline mr-1" />
                    Les entreprises seront importées avec le statut "En attente de validation"
                  </p>
                  <Button 
                    onClick={handleImport} 
                    disabled={!file || loading}
                    className="bg-[#635BFF] hover:bg-[#5248e5]"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Import en cours...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Importer
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            {result && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    {result.error_count === 0 ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    )}
                    Résultat de l'import
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Summary */}
                  <div className="flex gap-4">
                    <div className="flex-1 bg-green-50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-green-600">{result.imported_count}</p>
                      <p className="text-sm text-green-700">Importées</p>
                    </div>
                    <div className="flex-1 bg-red-50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-red-600">{result.error_count}</p>
                      <p className="text-sm text-red-700">Erreurs</p>
                    </div>
                  </div>

                  {/* Imported List */}
                  {result.imported && result.imported.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-green-700 mb-2 flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        Entreprises importées
                      </h4>
                      <div className="bg-green-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                        <ul className="text-sm space-y-1">
                          {result.imported.map((item, idx) => (
                            <li key={idx} className="text-green-800">
                              <span className="font-medium">{item.company}</span>
                              <span className="text-green-600 ml-2">({item.slug})</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Error List */}
                  {result.errors && result.errors.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-1">
                        <XCircle className="w-4 h-4" />
                        Lignes ignorées
                      </h4>
                      <div className="bg-red-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                        <ul className="text-sm space-y-2">
                          {result.errors.map((item, idx) => (
                            <li key={idx} className="text-red-800">
                              <span className="font-medium">Ligne {item.line}</span>
                              {item.company && <span className="ml-1">({item.company})</span>}
                              <span className="block text-red-600 text-xs">{item.error}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Action */}
                  {result.imported_count > 0 && (
                    <div className="pt-4 border-t">
                      <Button 
                        onClick={() => navigate('/admin/directory-management')}
                        variant="outline"
                      >
                        Voir les entreprises importées (en attente de validation)
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Instructions */}
          <div className="space-y-6">
            {/* Categories Reference */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Catégories disponibles</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Utilisez ces slugs dans la colonne "categories" (séparés par <code className="bg-gray-100 px-1 rounded">;</code>)
                </p>
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {categories.map((cat, idx) => (
                    <div key={idx} className="text-xs">
                      <code className="bg-gray-100 px-1 rounded text-[#635BFF]">{cat.slug}</code>
                      <span className="text-gray-500 ml-1 block text-[10px]">{cat.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Template Columns */}
            {template && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Colonnes du template</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    {template.columns.map((col, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        {col.required ? (
                          <span className="text-red-500 font-bold">*</span>
                        ) : (
                          <span className="text-gray-300">○</span>
                        )}
                        <div>
                          <code className="text-xs bg-gray-100 px-1 rounded">{col.name}</code>
                          <span className="text-gray-500 text-xs block">{col.description}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-gray-400 mt-3">* Champs obligatoires</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
